import { supabase } from './supabase'
import {
  absorbRemoteEvent,
  absorbCatchUpPage,
  markInFlight,
  markSynced,
  pendingOutbox,
  readCursor,
  recordOutboxFailure,
  writeEarlyCareShadow,
  writeNeedShadow,
  writePersonShadow,
} from './db'
import type {
  EventRow,
  InsertEventAck,
  JSONValue,
  EarlyCareState,
  Need,
  Person,
} from './types'
import type { RealtimeChannel } from '@supabase/supabase-js'

/**
 * SyncEngine: adapter over Supabase for BabyOS Person Graph.
 * 1. Push a local event via `insert_event()` PL/pgSQL RPC (canonical write path, DEC-015).
 * 2. Catch up missed events via `catch_up_events()` RPC (durable delivery, DEC-021).
 * 3. Subscribe to Realtime for cross-device events (fast but not durable — cursor is authority).
 * 4. Subscribe to persons / needs / early_care_state for shadow-table population.
 */

export interface PushResult {
  ack: InsertEventAck
}

interface RPCEventParams {
  p_id: string
  p_family_id: string
  p_person_id: string | null
  p_device_id: string
  p_writer_role: 'user' | 'device'
  p_event_type: string
  p_payload: JSONValue
  p_schema_version: number
  p_occurred_at: string
  p_recorded_at: string
  p_supersedes_event_id?: string | null
  p_source?: string
}

/** Push a single event via insert_event RPC. */
export async function pushEvent(params: RPCEventParams): Promise<InsertEventAck> {
  // Bound a hung request below the lease duration; ACK loss is a same-ID retry.
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const { data, error } = await Promise.race([
      supabase.rpc('insert_event', params),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error('insert_event timeout; confirmation unavailable')), 30_000)
      }),
    ])
    if (error) throw new Error(`insert_event failed: ${error.message}`)
    const rows = data as InsertEventAck[]
    const ack = Array.isArray(rows) && rows.length === 1 ? rows[0] : null
    if (!ack || ack.event_id !== params.p_id || ack.accepted !== true ||
        !Number.isSafeInteger(ack.server_sequence) || ack.server_sequence <= 0 ||
        !Number.isFinite(Date.parse(ack.server_received_at))) {
      throw new Error('insert_event returned invalid ACK')
    }
    return ack
  } finally {
    clearTimeout(timer)
  }
}

/** Drain the outbox until empty or no eligible rows. Self-serializes. */
let draining = false
let retryTimer: ReturnType<typeof setTimeout> | undefined
let scheduleGeneration = 0
async function scheduleOutboxRetry(generation: number): Promise<void> {
  if (typeof window === 'undefined') return
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return
  const { nextOutboxAttemptAt } = await import('./db')
  const at = await nextOutboxAttemptAt(session.user.id)
  if (generation !== scheduleGeneration || at === null) return
  // Foreground/background timer throttling is fine: expired leases remain eligible.
  retryTimer = setTimeout(() => {
    void drainOutbox().catch(() => { console.warn('[Wren] outbox retry unavailable') })
  }, Math.min(3_600_000, Math.max(1_000, at - Date.now())))
}
export async function drainOutbox(): Promise<{ pushed: number; failed: number }> {
  if (draining) return { pushed: 0, failed: 0 }
  clearTimeout(retryTimer)
  const generation = ++scheduleGeneration
  draining = true
  let pushed = 0
  let failed = 0
  try {
    for (;;) {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) break
      const batch = await pendingOutbox(20, session.user.id)
      if (batch.length === 0) break
      for (const evt of batch) {
        // Shared IndexedDB must not reattribute another account's pending events.
        const { data: current } = await supabase.auth.getSession()
        if (current.session?.user.id !== evt.user_id) continue
        const lease = await markInFlight(evt.id)
        if (!lease) continue
        try {
          const ack = await pushEvent({
            p_id: evt.id,
            p_family_id: evt.family_id,
            p_person_id: evt.person_id,
            p_device_id: evt.device_id,
            p_writer_role: evt.writer_role,
            p_event_type: evt.event_type,
            p_payload: JSON.parse(evt.payload_json) as JSONValue,
            p_schema_version: evt.schema_version,
            p_occurred_at: evt.occurred_at,
            p_recorded_at: evt.recorded_at,
            p_supersedes_event_id: evt.supersedes_event_id,
            p_source: evt.source,
          })
          await markSynced(evt.id, ack.server_sequence, ack.server_received_at)
          pushed++
        } catch (err) {
          await recordOutboxFailure(evt.id, err instanceof Error ? err.message : String(err), lease)
          failed++
        }
      }
    }
  } finally {
    draining = false
    void scheduleOutboxRetry(generation).catch(() => { console.warn('[Wren] outbox scheduling unavailable') })
  }
  return { pushed, failed }
}

/** Pull complete commit-safe pages. Share work per family, never drop a caller. */
const catchUps = new Map<string, { work: Promise<number>; rerun: boolean }>()
export function catchUp(familyId: string, limit = 500): Promise<number> {
  const existing = catchUps.get(familyId)
  if (existing) {
    // A reconnect may happen after the in-flight RPC took its snapshot. Run
    // another pass rather than losing the reconnect request to coalescing.
    existing.rerun = true
    return existing.work
  }
  // Match the server clamp, otherwise e.g. limit=5000 truncates after 2000 rows.
  const pageSize = Number.isFinite(limit) ? Math.min(2000, Math.max(1, Math.trunc(limit))) : 500
  const task = { work: Promise.resolve(0), rerun: false }
  task.work = (async () => {
    let total = 0
    do {
      task.rerun = false
      total += await pullCatchUpPages(familyId, pageSize)
    } while (task.rerun)
    return total
  })().finally(() => { catchUps.delete(familyId) })
  catchUps.set(familyId, task)
  return task.work
}

async function pullCatchUpPages(familyId: string, limit: number): Promise<number> {
  let totalAbsorbed = 0
  for (;;) {
    const since = await readCursor(familyId)
    // No fallback to v1: it cannot establish commit safety on an A8-only server.
    const { data, error } = await supabase.rpc('catch_up_events_v2', {
      p_family_id: familyId,
      p_since_sequence: since,
      p_limit: limit,
    })
    if (error) throw new Error(`catch_up_events_v2 failed: ${error.message}`)
    if (!Array.isArray(data)) throw new Error('catch_up_events_v2 returned a non-array')
    const rows = data as EventRow[]
    if (rows.length === 0) break
    await absorbCatchUpPage(familyId, since, rows)
    totalAbsorbed += rows.length
    if (rows.length < limit) break
  }
  return totalAbsorbed
}

// ==================== REALTIME SUBSCRIPTIONS ====================

export interface RealtimeHandles {
  events: RealtimeChannel
  persons: RealtimeChannel
  needs: RealtimeChannel
  earlyCareState: RealtimeChannel
}

/**
 * Subscribe to Realtime for a family. Populates shadow tables + calls onRemoteEvent
 * for cross-device events written by OTHER devices.
 * Returns handles you must call cleanup() on.
 */
export function subscribeFamily(
  familyId: string,
  deviceId: string,
  onRemoteEvent: (row: EventRow) => void,
): RealtimeHandles & { cleanup: () => Promise<void> } {
  // Events (cross-device notification)
  const events = supabase
    .channel(`family:events:${familyId}`)
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'events', filter: `family_id=eq.${familyId}` },
      async (payload) => {
        const row = payload.new as EventRow
        if (row.device_id === deviceId) return // skip our own events
        try {
          // Fast display only. Seeing N does not prove we persisted everything < N.
          await absorbRemoteEvent(row)
          onRemoteEvent(row)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[BabyOS] realtime absorb failure', err)
        }
      },
    )
    .subscribe((status) => {
      // Includes WebSocket reconnects while navigator.onLine stays true.
      if (status === 'SUBSCRIBED') {
        void catchUp(familyId).catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn('[BabyOS] realtime reconnect catch-up failure', err)
        })
      }
    })

  // Persons shadow
  const persons = supabase
    .channel(`family:persons:${familyId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'persons', filter: `family_id=eq.${familyId}` },
      async (payload) => {
        if (payload.eventType === 'DELETE') return
        await writePersonShadow(payload.new as Person)
      },
    )
    .subscribe()

  // Needs shadow
  const needs = supabase
    .channel(`family:needs:${familyId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'needs', filter: `family_id=eq.${familyId}` },
      async (payload) => {
        if (payload.eventType === 'DELETE') return
        await writeNeedShadow(payload.new as Need)
      },
    )
    .subscribe()

  // early_care_state shadow (per-person projection)
  const earlyCareState = supabase
    .channel(`family:ecs:${familyId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'early_care_state', filter: `family_id=eq.${familyId}` },
      async (payload) => {
        if (payload.eventType === 'DELETE') return
        await writeEarlyCareShadow(payload.new as EarlyCareState)
      },
    )
    .subscribe()

  return {
    events,
    persons,
    needs,
    earlyCareState,
    cleanup: async () => {
      await Promise.all([
        supabase.removeChannel(events),
        supabase.removeChannel(persons),
        supabase.removeChannel(needs),
        supabase.removeChannel(earlyCareState),
      ])
    },
  }
}
